/**
 * Comptes admin par resto (SuperAdmin, brique C) — LOGIQUE PURE.
 *
 * Le super_admin chapeaute les comptes ayant accès au back-office de chaque resto.
 * Aucune I/O ici (cf. accountsService.ts). Données via RPC `list_tenant_accounts`.
 */

/** Rôles possibles dans tenant_users (+ vestige 'superadmin' toléré). */
export type AccountRole = 'owner' | 'manager' | 'staff' | 'superadmin';

export const ACCOUNT_ROLE_LABELS: Record<AccountRole, string> = {
  owner: 'Propriétaire',
  manager: 'Gérant',
  staff: 'Équipier',
  superadmin: 'Super admin',
};

/** Libellé d'un rôle, tolérant à une valeur inconnue. */
export function roleLabel(role: string): string {
  return (ACCOUNT_ROLE_LABELS as Record<string, string>)[role] ?? role;
}

export interface TenantAccount {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  userId: string;
  email: string;
  fullName: string;
  role: string;
  createdAt: string | null;
}

interface RawAccount {
  tenant_id?: string;
  tenant_name?: string | null;
  tenant_slug?: string | null;
  user_id?: string;
  email?: string | null;
  full_name?: string | null;
  role?: string | null;
  created_at?: string | null;
}

/** Normalise une ligne brute de la RPC. */
export function toTenantAccount(raw: RawAccount): TenantAccount {
  return {
    tenantId: raw.tenant_id ?? '',
    tenantName: raw.tenant_name ?? '',
    tenantSlug: raw.tenant_slug ?? '',
    userId: raw.user_id ?? '',
    email: raw.email ?? '',
    fullName: raw.full_name ?? '',
    role: raw.role ?? '',
    createdAt: raw.created_at ?? null,
  };
}

/** Parse la réponse jsonb de la RPC (array, ou null) en liste typée. */
export function parseAccounts(data: unknown): TenantAccount[] {
  if (!Array.isArray(data)) return [];
  return data.map((r) => toTenantAccount(r as RawAccount));
}

/** Comptes d'un resto donné (les vrais comptes d'accès, hors super_admin réseau). */
export function accountsForTenant(list: TenantAccount[], tenantId: string): TenantAccount[] {
  return list.filter((a) => a.tenantId === tenantId && a.role !== 'superadmin');
}

/** Nom affichable d'un compte (nom complet, repli email). */
export function accountName(a: TenantAccount): string {
  return a.fullName.trim() || a.email || 'Compte sans nom';
}

/** Nombre de comptes par tenantId (hors super_admin réseau). */
export function countByTenant(list: TenantAccount[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const a of list) {
    if (a.role === 'superadmin') continue;
    out[a.tenantId] = (out[a.tenantId] ?? 0) + 1;
  }
  return out;
}
